import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Panel, Loader, Meter } from "@/components/portal/Shared";
import { EvidenceBadge } from "@/components/portal/EvidenceBadge";
import { evidenceOf } from "@/lib/evidence";
import { Users, FileStack, Award, Compass, Route, ArrowRight, Sparkle, UtensilsCrossed, Leaf, ChevronRight } from "lucide-react";

const STAT_ICONS = { ancestors: Users, records: FileStack, primary_sources: Award, active_leads: Compass, tribal_paths: Route };
const STAT_LABELS = { ancestors: "Ancestors Identified", records: "Records Uploaded", primary_sources: "Primary Sources", active_leads: "Active Research Leads", tribal_paths: "Tribal Research Paths" };

const IMG_BASE = process.env.REACT_APP_BACKEND_URL || "";

function RecipeOfDay({ recipe, onNavigate }) {
  if (!recipe) return null;
  return (
    <Panel className="p-0 overflow-hidden mb-8 border-[#D4AF37]/40" data-testid="recipe-of-day">
      <div className="grid sm:grid-cols-[240px_1fr]">
        <div className="relative h-44 sm:h-full min-h-[176px]">
          {recipe.image_url ? <img src={IMG_BASE + recipe.image_url} alt={recipe.name} className="absolute inset-0 h-full w-full object-cover" /> : null}
        </div>
        <div className="p-6">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[#FCF8EE] border border-[#D4AF37]/40 text-[#8F7220] px-3 py-1 text-[11px] font-bold uppercase tracking-wide mb-2">
            <Sparkle size={13} className="text-[#D4AF37]" /> Dish of the day
          </div>
          <h3 data-testid="recipe-of-day-name" className="font-serif text-2xl font-bold text-[#180D24]">{recipe.name}</h3>
          <div className="text-sm text-[#8F7220] font-mono mt-1">{recipe.tribe} · {recipe.region}</div>
          <p className="text-sm text-[#756D7E] mt-2 line-clamp-2">{recipe.about}</p>
          <button data-testid="recipe-of-day-view" onClick={() => onNavigate("tribal_recipes", { recipeId: recipe.id })}
            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#2A1442] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#482276] transition-colors">
            Cook this today <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </Panel>
  );
}

function PlantOfDay({ plant, onNavigate }) {
  if (!plant) return null;
  const nation = plant.region || plant.tribal_uses?.[0]?.nation;
  return (
    <Panel className="p-0 overflow-hidden mb-8 border-[#166534]/40" data-testid="plant-of-day">
      <div className="grid sm:grid-cols-[240px_1fr]">
        <div className="relative h-44 sm:h-full min-h-[176px] bg-[#F0F5F0]">
          {plant.image_url ? <img src={IMG_BASE + plant.image_url} alt={plant.common_name} className="absolute inset-0 h-full w-full object-cover" /> : null}
        </div>
        <div className="p-6">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[#F0F5F0] border border-[#166534]/40 text-[#166534] px-3 py-1 text-[11px] font-bold uppercase tracking-wide mb-2">
            <Leaf size={13} className="text-[#166534]" /> Plant of the day
          </div>
          <h3 data-testid="plant-of-day-name" className="font-serif text-2xl font-bold text-[#180D24]">{plant.common_name}</h3>
          <div className="text-sm text-[#166534] font-mono mt-1 italic">{plant.scientific_name}{nation ? ` · ${nation}` : ""}</div>
          <p className="text-sm text-[#756D7E] mt-2 line-clamp-2">{plant.use_summary}</p>
          <button data-testid="plant-of-day-view" onClick={() => onNavigate("tribal_remedies", { plantId: plant.id })}
            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#166534] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#14532D] transition-colors">
            Explore this plant <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </Panel>
  );
}

function HeritageFoods({ hf, onNavigate }) {
  if (!hf || !hf.affiliation || (!hf.recipes.length && !hf.plants.length)) return null;
  return (
    <Panel className="p-6 mb-8 border-[#D4AF37]/40 bg-gradient-to-br from-[#FCF8EE] to-white" data-testid="heritage-foods-strip">
      <div className="flex items-center gap-2 mb-1">
        <Sparkle size={18} className="text-[#D4AF37]" />
        <h3 className="font-serif text-xl font-semibold text-[#180D24]">Foods & plants of your nation</h3>
      </div>
      <p className="text-sm text-[#756D7E] mb-4">Drawn from your Tribal Affiliation — <span className="font-semibold text-[#59288E]">{hf.affiliation}</span>. Explore the traditional cuisine and plant knowledge tied to your documented heritage.</p>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#59288E] font-semibold text-sm mb-2"><UtensilsCrossed size={15} /> Recipes</div>
          <div className="space-y-1.5">
            {hf.recipes.slice(0, 4).map((r) => (
              <button key={r.id} data-testid={`hf-recipe-${r.id}`} onClick={() => onNavigate("tribal_recipes", { recipeId: r.id })}
                className="w-full text-left flex items-center justify-between gap-2 rounded-lg border border-[#E4DCD3] bg-white px-3 py-2 hover:border-[#D4AF37] transition-colors">
                <span className="text-sm text-[#1A171D] truncate font-medium">{r.name}</span><ChevronRight size={15} className="text-[#59288E] shrink-0" />
              </button>
            ))}
            {hf.recipes.length === 0 ? <div className="text-xs text-[#756D7E]">No matched recipes yet.</div> : null}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2 text-[#166534] font-semibold text-sm mb-2"><Leaf size={15} /> Plants</div>
          <div className="space-y-1.5">
            {hf.plants.slice(0, 4).map((p) => (
              <button key={p.id} data-testid={`hf-plant-${p.id}`} onClick={() => onNavigate("tribal_remedies", { plantId: p.id })}
                className="w-full text-left flex items-center justify-between gap-2 rounded-lg border border-[#E4DCD3] bg-white px-3 py-2 hover:border-[#166534] transition-colors">
                <span className="text-sm text-[#1A171D] truncate font-medium">{p.common_name}</span><ChevronRight size={15} className="text-[#166534] shrink-0" />
              </button>
            ))}
            {hf.plants.length === 0 ? <div className="text-xs text-[#756D7E]">No matched plants yet.</div> : null}
          </div>
        </div>
      </div>
    </Panel>
  );
}

export default function Journey({ onNavigate }) {
  const [data, setData] = useState(null);
  const [hf, setHf] = useState(null);
  useEffect(() => { api.get("/journey").then((r) => setData(r.data)); }, []);
  useEffect(() => { api.get("/heritage-foods").then((r) => setHf(r.data)).catch(() => {}); }, []);
  if (!data) return <Loader />;

  return (
    <div data-testid="module-journey">
      <PageHeader
        eyebrow={`${data.clan} • ${data.manifesto}`}
        title={<>Welcome back, <span className="text-[#59288E]">{data.user_name}</span></>}
        subtitle="Your evidence-based heritage journey at a glance. Keep building the documented record."
      />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {Object.entries(data.stats).map(([k, v], i) => {
          const Icon = STAT_ICONS[k];
          return (
            <Panel key={k} className="p-5 hp-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`stat-${k}`}>
              <Icon size={20} className="text-[#D4AF37] mb-3" />
              <div className="text-3xl font-serif font-bold text-[#180D24]">{v}</div>
              <div className="text-xs text-[#756D7E] mt-1">{STAT_LABELS[k]}</div>
            </Panel>
          );
        })}
      </div>

      <RecipeOfDay recipe={hf?.recipe_of_day} onNavigate={onNavigate} />
      <PlantOfDay plant={hf?.plant_of_day} onNavigate={onNavigate} />
      <HeritageFoods hf={hf} onNavigate={onNavigate} />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Score */}
        <Panel className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-serif text-xl font-semibold text-[#180D24]">Ancestral Research Score</h3>
            <div className="text-right">
              <div className="text-4xl font-serif font-bold text-[#59288E]">{data.score.overall}%</div>
              <div className="text-[10px] uppercase tracking-wider font-mono text-[#8F7220]">Completeness</div>
            </div>
          </div>
          <div className="space-y-4">
            {data.score.meters.map((m) => <Meter key={m.label} label={m.label} value={m.value} />)}
          </div>
          <div className="mt-5 rounded-xl bg-[#FEF2F2] border border-[#EF4444]/30 p-4 text-sm text-[#B91C1C]">
            <span className="font-semibold">Weakest link:</span> {data.score.weakest}
          </div>
          <p className="mt-3 text-[11px] text-[#756D7E]">This score measures research completeness — not "ancestry" or blood.</p>
        </Panel>

        {/* Evidence breakdown */}
        <Panel className="p-6">
          <h3 className="font-serif text-xl font-semibold text-[#180D24] mb-4">Evidence Breakdown</h3>
          <div className="space-y-2.5">
            {Object.entries(data.evidence_breakdown).map(([status, count]) => {
              const e = evidenceOf(status);
              return (
                <div key={status} className="flex items-center justify-between">
                  <EvidenceBadge status={status} />
                  <span className="font-mono font-bold text-lg" style={{ color: e.text }}>{count}</span>
                </div>
              );
            })}
          </div>
        </Panel>
      </div>

      {/* Continue + discovery */}
      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <Panel className="p-6 cursor-pointer hover:border-[#D4AF37] transition-colors" data-testid="continue-research" onClick={() => onNavigate("family_tree")}>
          <div className="text-xs uppercase tracking-wider font-mono text-[#8F7220] mb-2">Continue Research</div>
          {data.continue_research ? (
            <>
              <div className="font-serif text-lg font-semibold text-[#180D24] mb-3">{data.continue_research.title}</div>
              <EvidenceBadge status={data.continue_research.evidence_status} size="lg" />
            </>
          ) : (
            <div className="font-serif text-lg font-semibold text-[#180D24] mb-3">Start building your family tree</div>
          )}
          <div className="mt-4 text-sm text-[#59288E] flex items-center gap-1">Open workspace <ArrowRight size={15} /></div>
        </Panel>

        <Panel className="p-6" data-testid="continue-learning">
          <div className="text-xs uppercase tracking-wider font-mono text-[#8F7220] mb-3">Continue Learning</div>
          {data.continue_learning.map((c) => (
            <div key={c.title} className="mb-3 last:mb-0">
              <div className="text-sm font-medium text-[#180D24] mb-1">{c.title}</div>
              <div className="h-2 rounded-full bg-[#ECE5D8] overflow-hidden">
                <div className="h-full" style={{ width: `${c.progress}%`, background: "linear-gradient(90deg,#59288E,#D4AF37)" }} />
              </div>
            </div>
          ))}
        </Panel>

        <Panel className="p-6 hp-purple-panel text-white" data-testid="new-discovery">
          <div className="flex items-center gap-2 text-[#E7C965] mb-2">
            <Sparkle size={16} /><span className="text-xs uppercase tracking-wider font-mono">New Discovery</span>
          </div>
          <p className="text-sm leading-relaxed text-[#E9E2F2]">{data.new_discovery}</p>
          <button onClick={() => onNavigate("timeline")} className="mt-4 text-sm font-semibold text-[#E7C965] flex items-center gap-1 hover:gap-2 transition-all">
            Review connection <ArrowRight size={15} />
          </button>
        </Panel>
      </div>
    </div>
  );
}
